import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Cpu, Bot, Globe, Code, Shield, DollarSign } from "lucide-react";
import PageLayout from "@/components/PageLayout";

const CAPABILITIES = [
  { icon: Cpu, title: "Quantum-Optimized Routing", desc: "Leveraging quantum annealing to solve multi-provider routing as a combinatorial optimization problem, achieving near-optimal cost and latency in milliseconds." },
  { icon: Bot, title: "Agentic AI Orchestration", desc: "Autonomous AI agents that plan, execute, and adapt complex multi-step workflows without human intervention." },
  { icon: Globe, title: "Multi-Provider Intelligence", desc: "Real-time performance monitoring across 69+ AI providers with automatic failover, load balancing, and cost optimization." },
  { icon: Code, title: "Cross-Platform Compatibility", desc: "A single API endpoint that abstracts away provider differences \u2014 zero code changes required to switch or combine providers." },
  { icon: Shield, title: "Enterprise Security", desc: "SOC 2 Type II aligned practices, end-to-end encryption, and full audit logging for regulated industries." },
  { icon: DollarSign, title: "Cost Optimization", desc: "Typical savings of 40\u201370% on AI inference costs through intelligent model selection and token optimization." },
];

export default function CompanyPage() {
  const navigate = useNavigate();
  return (
    <PageLayout>
      <div className="max-w-5xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="company-heading">Built for the Future of AI</h1>
          <p className="text-lg text-[var(--qn-foreground-muted)] max-w-3xl mb-16 leading-relaxed">
            QNNEAL is the quantum-enhanced agentic AI platform developed by Global InTech AS &mdash; a Norwegian technology company pioneering the intersection of quantum computing and artificial intelligence.
          </p>
        </motion.div>

        <section className="mb-16">
          <h2 className="text-lg font-semibold mb-4 text-[var(--qn-primary)]">Our Mission</h2>
          <p className="text-[var(--qn-foreground-muted)] leading-relaxed">
            At QNNEAL, we believe that the next generation of AI infrastructure must be intelligent, autonomous, and cost-efficient. Our mission is to democratize access to quantum-optimized AI workflows, enabling organizations of every size to reduce costs, accelerate performance, and operate across 69+ providers without changing a single line of code.
          </p>
        </section>

        <section className="mb-16">
          <h2 className="text-lg font-semibold mb-4 text-[var(--qn-primary)]">What We Do</h2>
          <p className="text-[var(--qn-foreground-muted)] leading-relaxed mb-4">
            QNNEAL combines quantum annealing algorithms with agentic AI orchestration to intelligently route, optimize, and manage multi-provider AI workflows in real time. Our platform analyzes token usage, latency, cost, and quality metrics across providers &mdash; including OpenAI, Anthropic, Google, Mistral, Cohere, and hundreds more &mdash; to automatically select the optimal model and configuration for every task.
          </p>
          <p className="text-[var(--qn-foreground-muted)] leading-relaxed">
            Whether you are running a single inference call or orchestrating a complex multi-agent pipeline, QNNEAL ensures you get the best results at the lowest cost, with enterprise-grade reliability and security.
          </p>
        </section>

        <section className="mb-16">
          <h2 className="text-lg font-semibold mb-6 text-[var(--qn-primary)]">Key Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i }} className="p-5 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-surface)]">
                <c.icon className="w-8 h-8 text-[var(--qn-primary)] mb-3" />
                <h3 className="font-semibold text-sm mb-2">{c.title}</h3>
                <p className="text-sm text-[var(--qn-foreground-muted)] leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-lg font-semibold mb-4 text-[var(--qn-primary)]">The Company</h2>
          <p className="text-[var(--qn-foreground-muted)] leading-relaxed mb-4">
            Global InTech AS is a Norwegian limited liability company (organisasjonsnummer NO 935 706 998) headquartered in H&oslash;ylandsbygd, Norway. Founded with a commitment to advancing autonomous AI technology, Global InTech AS builds products that help enterprises harness the power of agentic AI for scalable growth and competitive advantage.
          </p>
          <p className="text-[var(--qn-foreground-muted)] leading-relaxed">
            Our team combines deep expertise in quantum computing, machine learning, distributed systems, and enterprise software to deliver solutions that are both cutting-edge and production-ready.
          </p>
        </section>

        <div className="flex gap-4 pt-4">
          <Button className="btn-primary" onClick={() => navigate("/login")} data-testid="company-cta-start">Get Started</Button>
          <Button variant="outline" onClick={() => navigate("/contact")} data-testid="company-cta-contact">Contact Us</Button>
        </div>
      </div>
    </PageLayout>
  );
}
